import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { ReleaseWhatsNewSlide } from './releaseWhatsNew.types';
import './release-whats-new.css';

type ReleaseWhatsNewModalProps = {
  isOpen: boolean;
  slides: ReleaseWhatsNewSlide[];
  onClose: () => void;
};

const ReleaseWhatsNewModal: React.FC<ReleaseWhatsNewModalProps> = ({
  isOpen,
  slides,
  onClose,
}) => {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setStepIndex(0);
    }
  }, [isOpen]);

  if (!slides.length) {
    return null;
  }

  const slide = slides[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === slides.length - 1;

  const handleClose = () => {
    onClose();
    setStepIndex(0);
  };

  const goNext = () => {
    if (isLast) {
      handleClose();
      return;
    }
    setStepIndex((current) => Math.min(current + 1, slides.length - 1));
  };

  const goBack = () => {
    setStepIndex((current) => Math.max(current - 1, 0));
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="release-whats-new-modal"
      overlayClassName="release-whats-new-overlay"
      ariaHideApp={false}
      contentLabel="What's new in this release"
    >
      <div className="release-whats-new">
        <header className="release-whats-new__header">
          <div>
            <p className="public-eyebrow mb-1">Release highlights</p>
            <h2 className="jo-heading m-0 text-xl font-black sm:text-2xl">
              {slide.title}
            </h2>
          </div>
          <button
            type="button"
            className="release-whats-new__close"
            onClick={handleClose}
            aria-label="Close what's new"
          >
            <CloseCircleOutlined />
          </button>
        </header>

        <SlideContent slide={slide} onNavigate={handleClose} />

        <div
          className="release-whats-new__dots"
          role="tablist"
          aria-label="Release tour steps"
        >
          {slides.map((item, index) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={index === stepIndex}
              aria-label={`${item.title}, step ${index + 1} of ${slides.length}`}
              className={`release-whats-new__dot${index === stepIndex ? ' is-active' : ''}`}
              onClick={() => setStepIndex(index)}
            />
          ))}
        </div>

        <footer className="release-whats-new__footer">
          <div className="release-whats-new__actions">
            {!isFirst && (
              <Button
                type="default"
                className="public-button-secondary"
                icon={<ArrowLeftOutlined />}
                onClick={goBack}
              >
                Back
              </Button>
            )}
            <Button
              type="primary"
              className="public-button-primary"
              icon={isLast ? <CheckCircleOutlined /> : <ArrowRightOutlined />}
              onClick={goNext}
            >
              {isLast ? 'Got it' : 'Next'}
            </Button>
          </div>
        </footer>
      </div>
    </ReactModal>
  );
};

const SlideContent: React.FC<{
  slide: ReleaseWhatsNewSlide;
  onNavigate: () => void;
}> = ({ slide, onNavigate }) => (
  <div className="release-whats-new__body">
    <p className="release-whats-new__description">{slide.description}</p>
    <ul className="release-whats-new__list">
      {slide.bullets.map((bullet) => (
        <li key={bullet}>
          <CheckCircleOutlined
            className="release-whats-new__bullet-icon"
            aria-hidden
          />
          <span>{bullet}</span>
        </li>
      ))}
    </ul>
    {slide.ctaLabel && slide.ctaPath && (
      <Link
        to={slide.ctaPath}
        className="release-whats-new__cta public-button-secondary"
        onClick={onNavigate}
      >
        {slide.ctaLabel}
      </Link>
    )}
  </div>
);

export default ReleaseWhatsNewModal;
